import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../auth/useAuth';
import { useRBAC } from '../auth/useRBAC';
import { isEffectivelyOnline, subscribeConnectivity } from '../offline/connectivity';
import { deliveryAttestationService } from '../services/deliveryAttestationService';
import DeliveryAttestationWall from '../pages/DeliveryAttestationWall';

const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';

/**
 * VALSOFT proof-of-delivery gate: a paid clinic must submit a one-time
 * attestation before OWNER/ADMIN users can keep using the web app.
 *
 * Mounted inside SubscriptionGuard (App.tsx), so /paywall,
 * /subscription/success and /onboarding are naturally outside the wall — the
 * form never interrupts checkout; it appears when the user lands back in the
 * app afterwards, on every visit, until completed.
 *
 * The gate is deliberately skipped offline/in grace mode — a compliance form
 * must never brick offline work (INV-19) — and the wall only renders on a
 * DEFINITIVE required:true from the backend (loading/error → app stays usable).
 */
const DeliveryAttestationGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { graceMode } = useAuth();
  const { claims, loading: rbacLoading } = useRBAC();
  const [online, setOnline] = useState(isEffectivelyOnline());
  useEffect(() => subscribeConnectivity(() => setOnline(isEffectivelyOnline())), []);

  // Only owners/admins can attest on behalf of the practice; vets/techs are
  // never walled.
  const canAttest = claims?.role === 'OWNER' || claims?.role === 'ADMIN';
  const active = !DEV_BYPASS && !graceMode && online && !rbacLoading && canAttest;

  // staleTime: Infinity — once attested the answer never changes, and checkout
  // upgrades return via a full Stripe redirect (fresh page load → fresh query).
  // The one path that flips "required" without a page load is a TRIALING→ACTIVE
  // conversion via Stripe webhook mid-session; that clinic gets walled on its
  // next page load, which is exactly the requested "next time they open the
  // app" semantics.
  const { data } = useQuery(['delivery-attestation'], deliveryAttestationService.getStatus, {
    enabled: active,
    staleTime: Infinity,
  });

  if (active && data?.required) {
    return <DeliveryAttestationWall />;
  }
  return <>{children}</>;
};

export default DeliveryAttestationGuard;
